import type {
  PublicHelpArticle,
  PublicHelpEventInput,
  PublicHelpIndex,
  PublicHelpSearchResult,
} from '@scrambl/shared/types/help-site'

import { API_URL } from './config'

const FETCH_TIMEOUT_MS = 10_000

export class HelpApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

async function fetchJson<T>(path: string): Promise<T> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const response = await fetch(`${API_URL}${path}`, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      throw new HelpApiError(response.status, `HTTP ${response.status}`)
    }
    return (await response.json()) as T
  } finally {
    clearTimeout(timeout)
  }
}

export function fetchIndex() {
  return fetchJson<PublicHelpIndex>('/public/help/index')
}

export function fetchArticle(id: string) {
  return fetchJson<PublicHelpArticle>(
    `/public/help/articles/${encodeURIComponent(id)}`,
  )
}

export function fetchSearch(query: string, moduleSlug?: string) {
  const params = new URLSearchParams({ q: query, limit: '10' })
  if (moduleSlug) params.set('moduleSlug', moduleSlug)
  return fetchJson<PublicHelpSearchResult[]>(
    `/public/help/search?${params.toString()}`,
  )
}

/** Fire-and-forget analytics; never throws, never blocks the UI. */
export function trackEvent(input: PublicHelpEventInput) {
  if (typeof window === 'undefined') return
  try {
    void fetch(`${API_URL}/public/help/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Telemetry is best-effort only.
  }
}
